/**
 * =============================================================================
 * useNavTree — Filtro de permissões + resolução da categoria ativa
 * Versão: 1.0.0 (2026-08-05)
 *
 * Descrição:
 *   Aplica sobre o NAV_CATEGORIES exatamente as MESMAS regras já usadas pelas
 *   sidebars legadas:
 *     - `usePermissions().canView(menuKey)` / `isAdmin` / `loading`
 *     - flags de módulo vindas de `useHeaderModuleFlags` (fonte única)
 *   Categorias sem nenhum filho visível são removidas (mesma regra de
 *   "grupo vazio some" das sidebars atuais).
 * =============================================================================
 */
import { useChecklistModuleEnabled } from '@/hooks/useChecklistModule';
// 2026-10-06: flag do módulo Estoque
import { useStockModuleEnabled } from '@/hooks/useStockModule';
import { useServiceOrderModuleEnabled } from '@/hooks/useServiceOrderModule';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { useHeaderModuleFlags } from '@/hooks/useHeaderModuleFlags';
import { NAV_CATEGORIES, type NavCategory, type NavItem } from '@/components/navigation/navRegistry';

interface UseNavTreeReturn {
  /** Categorias visíveis para o usuário atual (já filtradas). */
  categories: NavCategory[];
  /** Categoria correspondente à rota atual (match pelo prefixo mais longo). */
  activeCategoryId: string | null;
  /** Loading das permissões (para skeleton). */
  loading: boolean;
}

export function useNavTree(): UseNavTreeReturn {
  const { loading, canView, isAdmin } = usePermissions();
  const {
    isCrmAvailable,
    isFinancialAvailable,
    isTasksAvailable,
    isReferralAvailable,
    isActivitiesAvailable,
    isKnowledgeAvailable,
    isVehiclesAvailable,
    isRetentionAvailable,
    isLegalAvailable,
    isWorkflowAvailable,
  } = useHeaderModuleFlags();
  // Módulo Checklist — flag própria do tenant (Template de Checklist em Configurações)
  const isChecklistAvailable = useChecklistModuleEnabled();
  const isStockAvailable = useStockModuleEnabled();
  const isServiceOrdersAvailable = useServiceOrderModuleEnabled();
  const { pathname } = useLocation();

  // Mapa de flags de módulo → disponibilidade.
  const moduleFlags = useMemo(
    () => ({
      crm: isCrmAvailable,
      financial: isFinancialAvailable,
      tasks: isTasksAvailable,
      referral: isReferralAvailable,
      activities: isActivitiesAvailable,
      knowledge: isKnowledgeAvailable,
      vehicles: isVehiclesAvailable,
      retention: isRetentionAvailable,
      legal: isLegalAvailable,
      workflow: isWorkflowAvailable,
      checklist: isChecklistAvailable,
      stock: isStockAvailable,
      serviceOrders: isServiceOrdersAvailable,
    }),
    [
      isCrmAvailable,
      isFinancialAvailable,
      isTasksAvailable,
      isReferralAvailable,
      isActivitiesAvailable,
      isKnowledgeAvailable,
      isVehiclesAvailable,
      isRetentionAvailable,
      isLegalAvailable,
      isWorkflowAvailable,
      isChecklistAvailable,
      isStockAvailable,
      isServiceOrdersAvailable,
    ],
  );

  const categories = useMemo(() => {
    // Regra idêntica às sidebars legadas.
    const canSee = (menuKey: string | null | undefined) => {
      if (!menuKey) return true;
      if (loading) return isAdmin;
      if (isAdmin) return true;
      return canView(menuKey);
    };

    return NAV_CATEGORIES.filter((cat) => {
      if (cat.requiresModule && !moduleFlags[cat.requiresModule]) return false;
      // v1.2.0: categorias compartilhadas entre módulos (ex.: Clientes) são
      // exibidas quando AO MENOS UMA das flags listadas está ativa.
      if (
        cat.requiresAnyModule &&
        !cat.requiresAnyModule.some((flag) => moduleFlags[flag])
      ) {
        return false;
      }
      return true;
    })
      .map((cat) => {
        // v1.1.0: a seção especial (árvore de Workspaces → Pipelines do CRM)
        // também respeita permissão/módulo. Quando não permitida, o campo é
        // removido para que o painel não a renderize.
        const specialAllowed =
          !!cat.specialSection &&
          (!cat.specialSectionModule || moduleFlags[cat.specialSectionModule]) &&
          canSee(cat.specialSectionMenuKey);

        // v1.1.0: filtragem RECURSIVA (suporta grupos de nível 3).
        const filterItems = (items?: NavItem[]): NavItem[] | undefined =>
          items
            ?.filter(
              (item) =>
                canSee(item.menuKey) &&
                // Item também pode exigir flag de módulo (ex.: hub de Dashboards).
                (!item.requiresModule || moduleFlags[item.requiresModule]),
            )
            .map((item) => ({ ...item, children: filterItems(item.children) }))
            // Grupo (sem url) sem nenhum filho visível é removido.
            .filter((item) => item.url || (item.children?.length ?? 0) > 0);

        return {
          ...cat,
          specialSection: specialAllowed ? cat.specialSection : undefined,
          children: filterItems(cat.children),
        };

      })
      .filter((cat) => {
        // Seção especial visível: categoria permanece mesmo sem filhos.
        if (cat.specialSection) return true;
        // Link direto: filtra pela permissão da própria categoria.
        if (!cat.children) return canSee(cat.menuKey);
        // Categoria pai: some quando não sobra nenhum filho visível.
        return cat.children.length > 0;
      });
  }, [loading, isAdmin, canView, moduleFlags]);

  // Categoria ativa: prefixo mais longo que casa com a rota atual.
  // Desempate: categoria de LINK DIRETO (sem filhos) tem prioridade sobre
  // categoria-pai quando o prefixo casado tem o mesmo tamanho. Evita, por
  // exemplo, que "Clientes" (/tenant/crm/customers) ative o painel de CRM.
  const activeCategoryId = useMemo(() => {
    let bestId: string | null = null;
    let bestLen = -1;
    let bestIsDirect = false;

    for (const cat of categories) {
      const isDirect = !cat.children?.length && !cat.specialSection;
      // v1.1.0: coleta URLs de filhos E netos (grupos de nível 3).
      const collectUrls = (items?: typeof cat.children): string[] =>
        (items ?? []).flatMap((c) => [
          ...(c.url ? [c.url.split('?')[0]] : []),
          ...collectUrls(c.children),
        ]);

      const prefixes = [
        ...(cat.matchPrefixes ?? []),
        ...(cat.url ? [cat.url] : []),
        // Hubs (matchOnlyOwnPrefixes) não usam as URLs dos filhos, pois elas
        // pertencem a outras categorias.
        ...(cat.matchOnlyOwnPrefixes ? [] : collectUrls(cat.children)),
      ];


      for (const prefix of prefixes) {
        const isMatch = pathname === prefix || pathname.startsWith(`${prefix}/`);
        if (!isMatch) continue;
        const wins =
          prefix.length > bestLen ||
          (prefix.length === bestLen && isDirect && !bestIsDirect);
        if (wins) {
          bestLen = prefix.length;
          bestId = cat.id;
          bestIsDirect = isDirect;
        }
      }
    }

    return bestId;
  }, [categories, pathname]);

  return { categories, activeCategoryId, loading };
}
